import { useEffect, useState } from "react";
import useSWR from "swr";
import { X } from "lucide-react";
import { StatusBadge } from "../ui/status-badge";

import { useLocalStorage } from "@/components/useLocalStorage";
import { fetcherIgnore404, fetcherTyped } from "@/lib/utils";
import { AnnounceSchema } from "@/types/schemas";
import { ExternalLink } from "@/components/OptionalLink";

const BANNER_HEIGHT = "2.25rem";

/** Fixed announcement bar across the full width of the app. Shows the latests release, announcement or urgent post as a one-liner. */
export const AnnouncementBar = () => {
  const [mounted, setMounted] = useState(false);
  const { data: announcement } = useSWR(
    "/api/blog/announce",
    fetcherTyped(AnnounceSchema.nullable(), fetcherIgnore404),
    {
      // Actually revalidate when coming back to the tab, etc because there can be a new announcement.
      revalidateOnFocus: true,
      revalidateIfStale: true,
      revalidateOnReconnect: true
    }
  );
  const [dismissed, setDismissed] = useLocalStorage<string | null>(
    "announcement.dismissed",
    null
  );

  const visible = mounted && announcement && dismissed !== announcement.slug;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) {
      return;
    }

    const root = document.documentElement;
    if (visible) {
      root.style.setProperty("--banner-height", BANNER_HEIGHT);
    } else {
      root.style.removeProperty("--banner-height");
    }
  }, [mounted, visible]);

  const handleDismiss = () => {
    if (announcement) {
      setDismissed(announcement.slug);
    }
  };

  if (!announcement || dismissed === announcement.slug) {
    return null;
  }

  // z-40 stacks the bar above the header (z-30) and the journal drawer (z-35) but below
  // sheets and dialogs (z-50), so an open modal covers it instead of it punching through.
  // px-10 clears the absolutely positioned dismiss button on both sides so the centred
  // label can never run underneath it.
  return (
    <div className="fixed inset-x-0 top-0 z-40 flex h-9 items-center justify-center gap-2 border-b border-sidebar-border bg-sidebar px-2 text-sidebar-foreground shadow-sm">
      {announcement.type === "changelog" && announcement.version && (
        <StatusBadge status="new" customStatus={`Release ${announcement.version}`} />
      )}
      {announcement.type === "announcement" && (
        <StatusBadge status="draft" customStatus="Announcement" />
      )}
      {announcement.type === "urgent" && <StatusBadge status="error" customStatus="Urgent" />}
      <ExternalLink
        href={announcement.url}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex min-w-0 items-center gap-1 py-1 text-xs font-medium hover:underline"
      >
        <span className="line-clamp-1">{announcement.title}</span>
      </ExternalLink>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss announcement"
        className="absolute right-2 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
